import React, { useState, useMemo } from 'react';
import ToolLayout from './ToolLayout';

const TAX_BRACKETS = [
  { label: '24%', value: 0.24 },
  { label: '32%', value: 0.32 },
  { label: '35%', value: 0.35 },
  { label: '37%', value: 0.37 },
];

const MULTIPLIERS = [
  { label: '1.0×', value: 1.0, desc: 'Return of capital' },
  { label: '1.5×', value: 1.5, desc: 'Conservative' },
  { label: '2×', value: 2.0, desc: 'Moderate' },
  { label: '3×', value: 3.0, desc: 'Strong' },
  { label: '5×', value: 5.0, desc: 'Exceptional' },
];

const PA_CREDIT = 0.25;

function fmt(n) {
  return '$' + Math.round(n).toLocaleString('en-US');
}

function fmtPct(n) {
  return (n * 100).toFixed(1) + '%';
}

export default function InvestorDeckROI() {
  const [investment, setInvestment] = useState(100000);
  const [bracket, setBracket] = useState(0.37);
  const [isPA, setIsPA] = useState(false);
  const [multiplier, setMultiplier] = useState(2.0);
  const [years, setYears] = useState(3);

  const calc = useMemo(() => {
    const taxBenefit = investment * bracket;
    const paCredit = isPA ? investment * PA_CREDIT : 0;
    const totalTaxBenefit = taxBenefit + paCredit;
    const netEffectiveCost = Math.max(0, investment - totalTaxBenefit);

    const grossReturn = investment * multiplier;
    const profit = grossReturn - investment;
    const totalValue = profit + totalTaxBenefit;
    const netReturn = totalValue - netEffectiveCost;

    const annualizedROI = netEffectiveCost > 0
      ? Math.pow((totalValue / netEffectiveCost), 1 / years) - 1
      : 0;

    const effectiveCostReduction = totalTaxBenefit / investment;

    return { taxBenefit, paCredit, totalTaxBenefit, netEffectiveCost, grossReturn, profit, totalValue, netReturn, annualizedROI, effectiveCostReduction };
  }, [investment, bracket, isPA, multiplier, years]);

  const handlePrint = () => window.print();

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-page { background: white !important; color: black !important; min-height: 100vh; padding: 40px; }
          .print-card { border: 1px solid #ccc !important; background: white !important; }
          .print-white { color: black !important; }
          .print-accent { color: #0077cc !important; }
        }
      `}</style>

      <div className="no-print">
        <ToolLayout
          accent="Aperture Slate // Investor Tools"
          title="Investor Deck"
          titleAccent="ROI Summary"
          description="Configure your investment parameters and generate a print-ready summary to share with your financial advisor."
        >
          <div className="border border-zinc-800 bg-zinc-950 p-8 space-y-8 no-print">

            {/* Investment */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="text-[11px] tracking-widest text-zinc-400 uppercase font-bold">Investment Amount</label>
                <span className="text-2xl font-black tabular-nums">{fmt(investment)}</span>
              </div>
              <input
                type="range" min={10000} max={1000000} step={5000} value={investment}
                onChange={(e) => setInvestment(Number(e.target.value))}
                className="w-full h-1 appearance-none bg-zinc-800 rounded-full accent-sky-500 cursor-pointer"
              />
              <div className="flex justify-between text-[10px] text-zinc-600 mt-1">
                <span>$10K</span><span>$1M</span>
              </div>
            </div>

            {/* Tax Bracket */}
            <div>
              <label className="block text-[11px] tracking-widest text-zinc-400 uppercase font-bold mb-3">Federal Tax Bracket</label>
              <div className="grid grid-cols-4 gap-2">
                {TAX_BRACKETS.map((b) => (
                  <button
                    key={b.value}
                    onClick={() => setBracket(b.value)}
                    className={`py-3 text-sm font-bold tracking-widest border transition-all duration-200 ${
                      bracket === b.value
                        ? 'border-sky-500 text-sky-400 bg-sky-500/10'
                        : 'border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-zinc-300'
                    }`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>

            {/* PA Toggle */}
            <div className="flex items-center justify-between border border-zinc-800 px-5 py-4">
              <div>
                <p className="text-sm font-bold tracking-wider text-white">Filming in Pennsylvania?</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">Adds the PA 25% Production Tax Credit</p>
              </div>
              <button
                onClick={() => setIsPA((v) => !v)}
                className={`relative w-12 h-6 rounded-full transition-colors duration-300 focus:outline-none ${isPA ? 'bg-emerald-500' : 'bg-zinc-700'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-300 ${isPA ? 'translate-x-6' : 'translate-x-0'}`} />
              </button>
            </div>

            {/* Return Multiplier */}
            <div>
              <label className="block text-[11px] tracking-widest text-zinc-400 uppercase font-bold mb-3">
                Expected Return Multiplier
              </label>
              <div className="grid grid-cols-5 gap-2">
                {MULTIPLIERS.map((m) => (
                  <button
                    key={m.value}
                    onClick={() => setMultiplier(m.value)}
                    className={`py-3 text-center border transition-all duration-200 ${
                      multiplier === m.value
                        ? 'border-emerald-500 text-emerald-400 bg-emerald-500/10'
                        : 'border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-zinc-300'
                    }`}
                  >
                    <div className="text-sm font-black">{m.label}</div>
                    <div className="text-[9px] text-zinc-600 mt-0.5">{m.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Time Horizon */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="text-[11px] tracking-widest text-zinc-400 uppercase font-bold">Time Horizon</label>
                <span className="text-2xl font-black tabular-nums">{years} years</span>
              </div>
              <input
                type="range" min={1} max={7} step={1} value={years}
                onChange={(e) => setYears(Number(e.target.value))}
                className="w-full h-1 appearance-none bg-zinc-800 rounded-full accent-emerald-500 cursor-pointer"
              />
              <div className="flex justify-between text-[10px] text-zinc-600 mt-1">
                <span>1 yr</span><span>7 yrs</span>
              </div>
            </div>

            <div className="border-t border-zinc-800" />

            {/* Results */}
            <div className="space-y-3">
              <p className="text-[11px] tracking-widest text-zinc-400 uppercase font-bold">Summary</p>

              {[
                { label: 'Gross Investment', value: fmt(investment), color: 'text-white' },
                { label: `Federal Tax Deduction (${(bracket * 100).toFixed(0)}%)`, value: fmt(calc.taxBenefit), color: 'text-sky-400' },
                ...(isPA ? [{ label: 'PA Production Tax Credit (25%)', value: fmt(calc.paCredit), color: 'text-emerald-400' }] : []),
                { label: 'Total Tax Benefit', value: fmt(calc.totalTaxBenefit), color: 'text-sky-400' },
                { label: 'Net Effective Cost', value: fmt(calc.netEffectiveCost), color: 'text-white', bold: true },
                { label: `Gross Return at ${multiplier}×`, value: fmt(calc.grossReturn), color: 'text-white' },
                { label: 'Total Value (Return + Tax Benefit)', value: fmt(calc.totalValue), color: 'text-emerald-400', bold: true },
                { label: `Annualized ROI on Capital at Risk (${years}yr)`, value: fmtPct(calc.annualizedROI), color: 'text-emerald-400', bold: true },
              ].map((row) => (
                <div key={row.label} className="flex justify-between items-center py-2 border-b border-zinc-900">
                  <span className="text-[11px] text-zinc-400 tracking-wide">{row.label}</span>
                  <span className={`text-sm tabular-nums ${row.bold ? 'font-black text-base' : 'font-bold'} ${row.color}`}>{row.value}</span>
                </div>
              ))}
            </div>

            <button
              onClick={handlePrint}
              className="w-full py-4 bg-white text-black text-[11px] font-black tracking-widest uppercase hover:bg-sky-400 transition-colors"
            >
              Print / Save as PDF →
            </button>

            <p className="text-[10px] text-zinc-600 leading-relaxed tracking-wide border-t border-zinc-900 pt-4">
              Projections are illustrative only. IRC §181 eligibility depends on production classification and annual caps. Return multiples are not guaranteed. Film investment is high-risk and illiquid. Consult a tax professional and entertainment attorney before investing.
            </p>
          </div>
        </ToolLayout>
      </div>

      {/* Print-only view */}
      <div className="print-page hidden" style={{ display: 'none' }}>
        <style>{`@media print { .print-page { display: block !important; } }`}</style>
        <div style={{ fontFamily: 'Georgia, serif', maxWidth: '600px', margin: '0 auto' }}>
          <div style={{ borderBottom: '2px solid black', paddingBottom: '20px', marginBottom: '30px' }}>
            <p style={{ fontSize: '10px', letterSpacing: '3px', textTransform: 'uppercase', color: '#666', margin: 0 }}>Aperture Slate // Investor Summary</p>
            <h1 style={{ fontSize: '28px', fontWeight: 900, margin: '8px 0 0 0', textTransform: 'uppercase', letterSpacing: '-1px' }}>Film Investment ROI</h1>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <tbody>
              {[
                ['Investment Amount', fmt(investment)],
                ['Federal Tax Bracket', `${(bracket * 100).toFixed(0)}%`],
                ['Pennsylvania Credit', isPA ? 'Yes (25%)' : 'No'],
                ['Expected Return Multiplier', `${multiplier}×`],
                ['Time Horizon', `${years} years`],
                ['—', '—'],
                ['Federal Tax Deduction', fmt(calc.taxBenefit)],
                ...(isPA ? [['PA Production Tax Credit', fmt(calc.paCredit)]] : []),
                ['Total Tax Benefit', fmt(calc.totalTaxBenefit)],
                ['Net Effective Cost', fmt(calc.netEffectiveCost)],
                ['Cost Reduction via Tax Benefits', fmtPct(calc.effectiveCostReduction)],
                ['—', '—'],
                [`Gross Return at ${multiplier}×`, fmt(calc.grossReturn)],
                ['Total Value (Return + Tax Benefit)', fmt(calc.totalValue)],
                [`Annualized ROI (${years} yr)`, fmtPct(calc.annualizedROI)],
              ].map(([label, value], i) =>
                label === '—' ? (
                  <tr key={i}><td colSpan={2} style={{ borderBottom: '1px solid #ccc', padding: '6px 0' }} /></tr>
                ) : (
                  <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '10px 0', color: '#444' }}>{label}</td>
                    <td style={{ padding: '10px 0', textAlign: 'right', fontWeight: 'bold' }}>{value}</td>
                  </tr>
                )
              )}
            </tbody>
          </table>

          <p style={{ fontSize: '9px', color: '#999', marginTop: '30px', lineHeight: '1.6', borderTop: '1px solid #eee', paddingTop: '15px' }}>
            This summary is for illustrative purposes only. IRC §181 eligibility varies. Return multiples are not guaranteed. Film investment carries significant risk. Generated via Aperture Slate Filmmaker Tools. Consult qualified legal and tax professionals before making investment decisions.
          </p>
        </div>
      </div>
    </>
  );
}
